import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import {KTIcon} from '../../../_metronic/helpers'
import {getChatNotifications, getStatusNotifications} from '../../services/notification.service'
import {ChatNotification, StatusNotification, SortDirection} from '../../../types/notificationTypes'
import {useAuth} from '../../modules/auth'
import {ensureBenefitDetail} from '../../../utils/ensureBenefitDetail'
import './NotificationPage.css'

type TabType = 'chat' | 'status'

/**
 * Notification Page - Dribbble Style
 * Features: Tabs, Unified Search & Sorting, Infinite Scroll
 */
const NotificationPage: React.FC = () => {
  const navigate = useNavigate()
  const {auth} = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('chat')

  // Shared search & sort state
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [sortDirection, setSortDirection] = useState<SortDirection>('Desc')

  // Chat state
  const [chatNotifications, setChatNotifications] = useState<ChatNotification[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const [chatHasMore, setChatHasMore] = useState(true)
  const [chatCursor, setChatCursor] = useState<string | null>(null)

  // Status state
  const [statusNotifications, setStatusNotifications] = useState<StatusNotification[]>([])
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusHasMore, setStatusHasMore] = useState(true)
  const [statusCursor, setStatusCursor] = useState<string | null>(null)

  // Refs for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 500)
    return () => clearTimeout(timer)
  }, [search])

  // Reset data when search or sort changes
  useEffect(() => {
    setChatNotifications([])
    setChatCursor(null)
    setChatHasMore(true)
    setStatusNotifications([])
    setStatusCursor(null)
    setStatusHasMore(true)
  }, [searchDebounced, sortDirection])

  // Fetch chat notifications
  const fetchChatNotifications = useCallback(
    async (reset = false) => {
      if (chatLoading || (!reset && !chatHasMore)) return

      setChatLoading(true)
      try {
        const response = await getChatNotifications({
          limit: 20,
          cursor: reset ? undefined : chatCursor || undefined,
          claimNumber: searchDebounced || undefined,
          sortDirection,
        })

        if (response.isSuccess && response.data) {
          setChatNotifications((prev) =>
            reset ? response.data.items : [...prev, ...response.data.items]
          )
          setChatHasMore(response.data.hasMore)
          setChatCursor(response.data.nextCursor)
        }
      } catch (error) {
        console.error('Error fetching chat notifications:', error)
      } finally {
        setChatLoading(false)
      }
    },
    [chatLoading, chatHasMore, chatCursor, searchDebounced, sortDirection]
  )

  // Fetch status notifications
  const fetchStatusNotifications = useCallback(
    async (reset = false) => {
      if (statusLoading || (!reset && !statusHasMore)) return

      setStatusLoading(true)
      try {
        const response = await getStatusNotifications({
          limit: 20,
          cursor: reset ? undefined : statusCursor || undefined,
          claimNumber: searchDebounced || undefined,
          sortDirection,
        })

        if (response.isSuccess && response.data) {
          setStatusNotifications((prev) =>
            reset ? response.data.items : [...prev, ...response.data.items]
          )
          setStatusHasMore(response.data.hasMore)
          setStatusCursor(response.data.nextCursor)
        }
      } catch (error) {
        console.error('Error fetching status notifications:', error)
      } finally {
        setStatusLoading(false)
      }
    },
    [statusLoading, statusHasMore, statusCursor, searchDebounced, sortDirection]
  )

  // Initial fetch when tab or search/sort changes
  useEffect(() => {
    if (activeTab === 'chat') {
      fetchChatNotifications(true)
    } else {
      fetchStatusNotifications(true)
    }
  }, [activeTab, searchDebounced, sortDirection])

  // Infinite scroll
  useEffect(() => {
    const loading = activeTab === 'chat' ? chatLoading : statusLoading
    const hasMore = activeTab === 'chat' ? chatHasMore : statusHasMore

    if (loading) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          if (activeTab === 'chat') {
            fetchChatNotifications()
          } else {
            fetchStatusNotifications()
          }
        }
      },
      {threshold: 0.1}
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [activeTab, chatLoading, statusLoading, chatHasMore, statusHasMore])

  // Navigate to claim detail - fetch claim details first (like NotificationCard)
  const handleNotificationClick = async (notification: ChatNotification | StatusNotification) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/GetByClaimNumber/${notification.claimNumber}`
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}`,
        },
      })

      if (response.data) {
        if (!ensureBenefitDetail(response.data)) return

        navigate('/inpatient', {state: {claimDetails: response.data}})
      }
    } catch (error) {
      console.error('Error fetching claim details:', error)
      // Fallback to claimNumber if API fails
      navigate('/inpatient', {state: {claimNumber: notification.claimNumber}})
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const currentData = activeTab === 'chat' ? chatNotifications : statusNotifications
  const isLoading = activeTab === 'chat' ? chatLoading : statusLoading

  return (
    <div className='notification-page'>
      {/* Header */}
      <div className='notification-header'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-4'>
          <div>
            <h1 className='fw-bold text-dark mb-1'>Notifications</h1>
            <p className='text-gray-500 fs-6 mb-0'>Stay updated with your claims</p>
          </div>

          {/* Search & Sort Controls */}
          <div className='d-flex align-items-center gap-3'>
            <div className='position-relative'>
              <KTIcon
                iconName='magnifier'
                className='fs-3 position-absolute top-50 translate-middle-y ms-4 text-gray-500'
              />
              <input
                type='text'
                className='form-control form-control-solid ps-12'
                placeholder='Search by claim number...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{minWidth: '280px', borderRadius: '10px'}}
              />
            </div>
            <button
              className='btn btn-light-primary d-flex align-items-center gap-2'
              onClick={() => setSortDirection((prev) => (prev === 'Desc' ? 'Asc' : 'Desc'))}
              style={{borderRadius: '10px'}}
            >
              <KTIcon
                iconName={sortDirection === 'Desc' ? 'arrow-down' : 'arrow-up'}
                className='fs-3'
              />
              {sortDirection === 'Desc' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className='notification-tabs mt-8'>
          <button
            className={`notification-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <KTIcon iconName='message-text-2' className='fs-3 me-2' />
            Chat Messages
            {chatNotifications.filter((n) => !n.isOpen).length > 0 && (
              <span className='badge badge-primary ms-2'>
                {chatNotifications.filter((n) => !n.isOpen).length}
              </span>
            )}
          </button>
          <button
            className={`notification-tab ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            <KTIcon iconName='notification-status' className='fs-3 me-2' />
            Status Updates
            {statusNotifications.filter((n) => !n.isOpen).length > 0 && (
              <span className='badge badge-warning ms-2'>
                {statusNotifications.filter((n) => !n.isOpen).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className='notification-list-container'>
        {currentData.length === 0 && !isLoading ? (
          <div className='notification-empty'>
            <KTIcon
              iconName={activeTab === 'chat' ? 'message-text-2' : 'notification-status'}
              className='fs-3x text-gray-400 mb-4'
            />
            <h3 className='text-gray-600 fw-semibold'>No notifications</h3>
            <p className='text-gray-500'>
              {search ? 'No results found for your search' : "You're all caught up!"}
            </p>
          </div>
        ) : (
          <div className='notification-list'>
            {currentData.map((notification, index) => (
              <div
                key={`${notification.claimNumber}-${index}`}
                className={`notification-card ${!notification.isOpen ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className='notification-icon'>
                  <div
                    className={`icon-wrapper ${
                      activeTab === 'chat' ? 'bg-light-primary' : 'bg-light-warning'
                    }`}
                  >
                    <KTIcon
                      iconName={activeTab === 'chat' ? 'message-text-2' : 'notification-status'}
                      className={`fs-2 ${activeTab === 'chat' ? 'text-primary' : 'text-warning'}`}
                    />
                  </div>
                </div>

                <div className='notification-content'>
                  <div className='d-flex align-items-center justify-content-between mb-1'>
                    <span className='fw-bold text-dark fs-6'>{notification.claimNumber}</span>
                    <span className='text-gray-500 fs-8'>{formatDate(notification.createdAt)}</span>
                  </div>
                  <p className='text-gray-600 fs-7 mb-2 text-truncate-2'>
                    {notification.remark || 'New notification'}
                  </p>
                  <div className='d-flex align-items-center justify-content-between'>
                    <span className='text-gray-500 fs-8'>
                      {'insurerName' in notification
                        ? notification.insurerName
                        : notification.providerName}
                    </span>
                    {!notification.isOpen && (
                      <span
                        className={`badge ${
                          activeTab === 'chat' ? 'badge-primary' : 'badge-warning'
                        }`}
                      >
                        New
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={loadMoreRef} />
            {isLoading && (
              <div className='notification-loading'>
                <span className='spinner-border spinner-border-sm text-primary' />
                <span className='ms-2 text-gray-500'>Loading more...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationPage
